/**
 * Reading a plan map as a picture: the map's canvas and the 3D overlay's, which react-three-map draws
 * on a canvas of its own. Moved from the corner plan's capture host (`PlanPictureCapture`,
 * `spec-onboarding-3d-plan-corner-plan-snapshot`) when Position on map's opening view picture
 * (`retire-edit-mode-opening-view`, R9) needed the same read: one copy, used by both.
 *
 * Both canvases must keep their drawing buffer (`PlanMapHost`'s `preserveDrawingBuffer`).
 */
import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import type { Object3D } from 'three';

/** Hands out the 3D overlay's canvas and scene: react-three-map draws the plan on a canvas of its own. */
export const OverlayCanvas = ({
  onCanvas,
  sceneRef,
}: {
  onCanvas: (canvas: HTMLCanvasElement | null) => void;
  sceneRef: { current: Object3D | null };
}) => {
  const canvas = useThree(s => s.gl.domElement);
  const scene = useThree(s => s.scene);
  sceneRef.current = scene;
  useEffect(() => {
    onCanvas(canvas);
    return () => onCanvas(null);
  }, [canvas, onCanvas]);
  return null;
};

/**
 * The map's canvas and the overlay's, stacked as they are shown, on a canvas of `width` × `height`
 * pixels: the one compositing implementation (`spec-onboarding-persistent-scene-handover`, What This
 * Replaces). `baseShown` false leaves the street map out, as a host with it hidden shows the plan
 * alone (`PlanMapHost`'s `basemapHidden`). `null` when the browser gives no 2D context.
 */
export const composeMapPicture = (
  base: HTMLCanvasElement,
  overlay: HTMLCanvasElement | null,
  width: number,
  height: number,
  { baseShown = true }: { baseShown?: boolean } = {},
): HTMLCanvasElement | null => {
  const out = document.createElement('canvas');
  out.width = width;
  out.height = height;
  const ctx = out.getContext('2d');
  if (!ctx) return null;
  if (baseShown) ctx.drawImage(base, 0, 0, width, height);
  if (overlay) ctx.drawImage(overlay, 0, 0, width, height);
  return out;
};

/** The map's canvas and the overlay's, stacked as they are shown, as a PNG. */
export const readMapPicture = (
  base: HTMLCanvasElement,
  overlay: HTMLCanvasElement | null,
  width: number,
  height: number,
): Promise<Blob | null> => {
  const out = composeMapPicture(base, overlay, width, height);
  if (!out) return Promise.resolve(null);
  return new Promise(resolve => out.toBlob(resolve, 'image/png'));
};
